/**
 * Anneau de répartition — la part de chaque entrée dans un tout.
 *
 * Réservé au part-à-tout à six parts au plus (règle du skill dataviz) : au-delà,
 * les parts voisines se confondent, et un tableau lit mieux. Le total est écrit
 * au centre ; la légende porte chaque montant ET sa part, pour que rien ne
 * dépende de la seule couleur ni du survol.
 *
 * Une entrée à zéro (source pas encore branchée) reste dans la légende avec sa
 * mention, sans part dans l'anneau.
 */

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { formatPercent } from '../lib/format';
import { COLORS } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Remplace le montant dans la légende, ex. « à venir ». */
  note?: string;
}

interface DonutChartProps {
  title: string;
  subtitle: string;
  slices: readonly DonutSlice[];
  formatValue: (value: number) => string;
  emptyMessage?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: DonutSlice & { share: number } }>;
  formatValue: (value: number) => string;
}

function SliceTooltip({ active, payload, formatValue }: TooltipProps) {
  const slice = payload?.[0]?.payload;
  if (!active || !slice) return null;
  return (
    <div className="tooltip" role="tooltip">
      <div className="tooltip__row">
        <span className="legend__key" style={{ background: slice.color }} aria-hidden="true" />
        <span className="tooltip__name">{slice.label}</span>
        <span className="tooltip__value">{formatValue(slice.value)}</span>
      </div>
      <p className="tooltip__title">{formatPercent(slice.share)} du total</p>
    </div>
  );
}

export function DonutChart({ title, subtitle, slices, formatValue, emptyMessage }: DonutChartProps) {
  // Une part négative (remboursements supérieurs aux ventes) n'a pas de sens
  // dans un anneau : elle compte pour zéro dans le dessin, la légende la dit.
  const total = slices.reduce((sum, slice) => sum + Math.max(0, slice.value), 0);
  const rows = slices.map((slice) => ({ ...slice, share: total > 0 ? (Math.max(0, slice.value) / total) * 100 : 0 }));
  const drawn = rows.filter((slice) => slice.value > 0);

  const columns: ReadonlyArray<DataColumn<(typeof rows)[number]>> = [
    { key: 'label', header: 'Entrée', render: (row) => row.label },
    { key: 'value', header: 'Montant', numeric: true, render: (row) => row.note ?? formatValue(row.value) },
    { key: 'share', header: 'Part', numeric: true, render: (row) => (row.note ? '—' : formatPercent(row.share)) },
  ];

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      rows={drawn.length > 0 ? rows : []}
      columns={columns}
      emptyMessage={emptyMessage ?? 'Aucune vente sur cette période.'}
      chartHeight={300}
      className="donut-card"
    >
      <div className="donut">
        <div className="donut__chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={drawn}
                dataKey="value"
                nameKey="label"
                innerRadius="62%"
                outerRadius="92%"
                startAngle={90}
                endAngle={-270}
                isAnimationActive={false}
                // Filet de 2 px couleur surface entre les parts : deux parts
                // voisines ne se touchent jamais, même en vision des couleurs
                // déficiente.
                stroke={COLORS.surface}
                strokeWidth={2}
              >
                {drawn.map((slice) => (
                  <Cell key={slice.key} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip wrapperStyle={{ zIndex: 5 }} content={<SliceTooltip formatValue={formatValue} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="donut__center" aria-hidden="true">
            <span className="donut__total">{formatValue(total)}</span>
            <span className="donut__caption">total</span>
          </div>
        </div>

        <ul className="donut__legend">
          {rows.map((slice) => (
            <li key={slice.key} className="donut__legend-item">
              <span
                className="legend__key legend__key--area"
                style={{ background: slice.note ? 'transparent' : slice.color, borderColor: slice.color }}
                aria-hidden="true"
              />
              <span className="donut__legend-label">{slice.label}</span>
              <span className="donut__legend-value">{slice.note ?? formatValue(slice.value)}</span>
              <span className="donut__legend-share">{slice.note ? '' : formatPercent(slice.share)}</span>
            </li>
          ))}
        </ul>
      </div>
    </ChartCard>
  );
}
